import React, { useState, useEffect } from 'react';
import { getTenantUsers, updateUserRole, updateUserStatus, updateUserScope } from '../../services/firestore/userService';
import { getTenantPermissions, subscribeToTenantPermissions } from '../../services/firestore/permissionService';
import { getAllFactories } from '../../services/firestore/factoryService';
import { getAllDoorGroups } from '../../services/firestore/doorGroupService';
import { getAllDocks } from '../../services/firestore/dockService';
import { getAllTransporters } from '../../services/firestore/transporterService';
import { ROLES, PERMISSIONS } from '../../constants/permissions';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { adminCreateUser, adminResetPasswordEmail } from '../../services/auth/adminAuthService';
import { parseUsersExcel, bulkCreateUsers, downloadUserTemplate } from '../../services/business/userUploadService';
import { useToast } from '../../hooks/useToast';
import MultiSelectCombobox from '../../components/shared/MultiSelectCombobox';
import Combobox from '../../components/shared/Combobox';

const UserManagement = ({ userData }) => {
    const { can } = usePermissions(userData);
    const [users, setUsers] = useState([]);
    const [factories, setFactories] = useState([]);
    const [doorGroups, setDoorGroups] = useState([]);
    const [docks, setDocks] = useState([]);
    const [transporters, setTransporters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [scopeModal, setScopeModal] = useState({ show: false, user: null });
    const [selectedScope, setSelectedScope] = useState({ factoryIds: [], doorGroupIds: [], dockIds: [], transporterId: '' });
    const { success, error: toastError, warning } = useToast();

    // New Modals State
    const [addModal, setAddModal] = useState(false);
    const [uploadModal, setUploadModal] = useState({ show: false, file: null, parsedUsers: [], progress: 0 });
    const [newUser, setNewUser] = useState({ fullName: '', email: '', password: '', role: 'guest', transporterId: '' });
    const [isProcessing, setIsProcessing] = useState(false);

    // Filters State
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [scopeFilter, setScopeFilter] = useState('all'); // Filter by a specific factory/dock/doorGroup id
    const [transporterFilter, setTransporterFilter] = useState('all');

    // Dialog States
    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        action: null,
        showCancel: true
    });

    const [availableRoles, setAvailableRoles] = useState(Object.values(ROLES));
    const [transporterRoles, setTransporterRoles] = useState([]);

    useEffect(() => {
        loadData();
    }, [userData?.tenantId]);

    const loadData = async () => {
        if (!userData?.tenantId) return;
        setLoading(true);
        try {
            const [usersData, factoriesData, doorGroupsData, docksData, transportersData, permResult] = await Promise.all([
                getTenantUsers(userData.tenantId),
                getAllFactories(userData.tenantId, true),
                getAllDoorGroups(userData.tenantId, true),
                getAllDocks(userData.tenantId),
                getAllTransporters(userData.tenantId, true),
                getTenantPermissions(userData.tenantId)
            ]);
            setUsers(usersData);
            setFactories(factoriesData);
            setDoorGroups(doorGroupsData);
            setDocks(docksData);
            setTransporters(transportersData);

            // Derive roles from permissions + system defaults
            if (permResult && permResult.rolePermissions) {
                const dbRoles = Object.keys(permResult.rolePermissions);
                const systemRoles = Object.values(ROLES);
                const allRoles = Array.from(new Set([...systemRoles, ...dbRoles]));
                setAvailableRoles(allRoles);
                setTransporterRoles(permResult.transporterRoles || []);
            }
        } catch (error) {
            console.error('Veriler yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        setUpdatingId(userId);
        try {
            await updateUserRole(userData.tenantId, userId, newRole);
            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
        } catch (error) {
            toastError('Rol güncellenirken bir hata oluştu.');
        } finally {
            setUpdatingId(null);
        }
    };

    const openScopeModal = (user) => {
        setScopeModal({ show: true, user });
        setSelectedScope(user.scope || { factoryIds: [], doorGroupIds: [], dockIds: [], transporterId: '' });
    };

    const handleScopeChange = (type, newIds) => {
        setSelectedScope(prev => ({
            ...prev,
            [type]: newIds
        }));
    };

    const handleSaveScope = async () => {
        setUpdatingId(scopeModal.user.id);
        try {
            await updateUserScope(userData.tenantId, scopeModal.user.id, selectedScope);
            setUsers(users.map(u => u.id === scopeModal.user.id ? { ...u, scope: selectedScope } : u));
            setScopeModal({ show: false, user: null });
            success('Veri kapsamı güncellendi.');
        } catch (error) {
            toastError('Kapsam güncellenirken bir hata oluştu.');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleStatusToggle = async (userId, currentStatus) => {
        setUpdatingId(userId);
        try {
            await updateUserStatus(userData.tenantId, userId, !currentStatus);
            setUsers(users.map(u => u.id === userId ? { ...u, isActive: !currentStatus } : u));
            success(currentStatus ? 'Kullanıcı durduruldu.' : 'Kullanıcı aktifleştirildi.');
        } catch (error) {
            toastError('Durum güncellenemedi.');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleInlineTransporterChange = async (userId, transporterId) => {
        setUpdatingId(userId);
        try {
            const user = users.find(u => u.id === userId);
            const newScope = { ...(user.scope || {}), transporterId };
            await updateUserScope(userData.tenantId, userId, newScope);
            setUsers(users.map(u => u.id === userId ? { ...u, scope: newScope } : u));
            success('Nakliyeci ataması güncellendi.');
        } catch (error) {
            toastError('Güncelleme başarısız.');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        try {
            // Include transporterId in scope if role is a transporter role
            const userToCreate = { ...newUser, tenantId: userData.tenantId };
            const isTransporterRole = transporterRoles.includes(newUser.role);

            if (isTransporterRole) {
                if (!newUser.transporterId) {
                    toastError('Lütfen bir nakliye firması seçin.');
                    setIsProcessing(false);
                    return;
                }
                userToCreate.scope = { transporterId: newUser.transporterId };
            }

            const created = await adminCreateUser(userToCreate);
            setUsers([...users, created]);
            setAddModal(false);
            setNewUser({ fullName: '', email: '', password: '', role: 'guest', transporterId: '' });
            success('Kullanıcı başarıyla oluşturuldu.');
        } catch (error) {
            toastError('Kullanıcı oluşturulamadı: ' + error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const parsed = await parseUsersExcel(file);
            setUploadModal(prev => ({ ...prev, file, parsedUsers: parsed, show: true }));
        } catch (error) {
            toastError(error.message);
        }
    };

    const handleBulkUpload = async () => {
        setIsProcessing(true);
        try {
            const results = await bulkCreateUsers(
                uploadModal.parsedUsers,
                userData.tenantId,
                (curr, total) => setUploadModal(prev => ({ ...prev, progress: Math.round((curr / total) * 100) }))
            );

            if (results.errors.length > 0) {
                warning(`${results.success.length} kullanıcı eklendi, ${results.errors.length} hata oluştu.`);
            } else {
                success(`${results.success.length} kullanıcı başarıyla eklendi.`);
            }

            loadData(); // Refresh list
            setUploadModal({ show: false, file: null, parsedUsers: [], progress: 0 });
        } catch (error) {
            toastError('Toplu kayıt sırasında hata: ' + error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handlePasswordReset = async (email) => {
        setConfirmConfig({
            isOpen: true,
            title: 'Şifre Sıfırlama',
            message: `${email} adresine şifre sıfırlama e-postası gönderilsin mi?`,
            type: 'info',
            action: async () => {
                try {
                    await adminResetPasswordEmail(email);
                    success('Şifre sıfırlama e-postası gönderildi.');
                } catch (error) {
                    toastError('E-posta gönderilemedi.');
                }
            }
        });
    };

    // Filter Logic
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = roleFilter === 'all' || user.role === roleFilter;

        let matchesScope = true;
        if (scopeFilter !== 'all') {
            const scope = user.scope || {};
            const factories = scope.factoryIds || [];
            const doorGroups = scope.doorGroupIds || [];
            const docks = scope.dockIds || [];
            const transporterId = scope.transporterId || '';

            matchesScope = factories.includes(scopeFilter) ||
                doorGroups.includes(scopeFilter) ||
                docks.includes(scopeFilter);
        }

        const matchesTransporter = transporterFilter === 'all' ||
            (user.scope?.transporterId === transporterFilter);

        return matchesSearch && matchesRole && matchesScope && matchesTransporter;
    });

    // Helper to get scope display names
    const getScopeDisplay = (scope) => {
        if (!scope) return <span className="text-xs text-secondary italic">Tüm Tesis</span>;

        const items = [];
        if (scope.transporterId) {
            const t = transporters.find(x => x.id === scope.transporterId);
            if (t) items.push(`🚛 ${t.companyName}`);
        }
        if (scope.factoryIds?.length > 0) {
            items.push(`🏭 ${scope.factoryIds.length} Fabrika`);
        }
        if (scope.doorGroupIds?.length > 0) {
            items.push(`🚪 ${scope.doorGroupIds.length} Grup`);
        }
        if (scope.dockIds?.length > 0) {
            items.push(`⚓ ${scope.dockIds.length} Rampa`);
        }

        return items.length > 0 ? (
            <div className="flex flex-wrap gap-xs">
                {items.map((item, idx) => (
                    <span key={idx} className="badge-outline text-2xs px-xs py-0" style={{ fontSize: '10px' }}>{item}</span>
                ))}
            </div>
        ) : <span className="text-xs text-secondary italic">Kapsam Yok</span>;
    };

    if (loading) return <div className="p-xl text-center">Yükleniyor...</div>;

    return (
        <div className="container p-xl">
            <div className="flex justify-between items-center mb-xl">
                <div>
                    <h1 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>Kullanıcı Yönetimi</h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Tesisinizdeki kullanıcıların rollerini ve erişim durumlarını yönetin.</p>
                </div>
                <div className="flex gap-md">
                    <Button variant="secondary" onClick={downloadUserTemplate}>
                        📥 Şablon İndir
                    </Button>
                    <div style={{ position: 'relative' }}>
                        <Button variant="secondary" onClick={() => document.getElementById('excel-upload').click()}>
                            📊 Excel'den Aktar
                        </Button>
                        <input
                            id="excel-upload"
                            type="file"
                            hidden
                            accept=".xlsx, .xls"
                            onChange={handleFileChange}
                        />
                    </div>
                    <Button variant="primary" onClick={() => setAddModal(true)}>
                        ➕ Yeni Kullanıcı
                    </Button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="card-premium glow-theme p-lg mb-xl">
                <div className="flex flex-wrap gap-lg items-end">
                    <div className="flex-1 min-w-[300px]">
                        <label className="block text-xs font-bold text-secondary uppercase tracking-wider mb-xs">🔍 Kullanıcı Ara</label>
                        <input
                            type="text"
                            className="input"
                            style={{
                                height: '42px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderColor: 'var(--border-color)',
                                borderRadius: '12px'
                            }}
                            placeholder="İsim veya e-posta ile ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="w-[200px]">
                        <label className="block text-xs font-bold text-secondary uppercase tracking-wider mb-xs">🎭 Rol Filtresi</label>
                        <select
                            className="select"
                            style={{
                                height: '42px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderColor: 'var(--border-color)',
                                borderRadius: '12px'
                            }}
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                        >
                            <option value="all">Tüm Roller</option>
                            {availableRoles.map(role => (
                                <option key={role} value={role}>{role.replace(/_/g, ' ').toUpperCase()}</option>
                            ))}
                        </select>
                    </div>

                    <div className="w-[200px]">
                        <label className="block text-xs font-bold text-secondary uppercase tracking-wider mb-xs">🚛 Nakliyeci</label>
                        <Combobox
                            items={transporters}
                            value={transporterFilter === 'all' ? '' : transporters.find(t => t.id === transporterFilter)?.companyName || ''}
                            onChange={(item) => setTransporterFilter(item ? item.id : 'all')}
                            onInputChange={(val) => { if (!val) setTransporterFilter('all'); }}
                            placeholder="Nakliyeci ara..."
                            labelKey="companyName"
                            valueKey="id"
                            inputStyle={{
                                height: '42px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderColor: 'var(--border-color)',
                                borderRadius: '12px',
                                color: 'var(--text-primary)'
                            }}
                        />
                    </div>

                    <div className="w-[240px]">
                        <label className="block text-xs font-bold text-secondary uppercase tracking-wider mb-xs">📍 Kapsam Filtresi</label>
                        <select
                            className="select"
                            style={{
                                height: '42px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderColor: 'var(--border-color)',
                                borderRadius: '12px'
                            }}
                            value={scopeFilter}
                            onChange={(e) => setScopeFilter(e.target.value)}
                        >
                            <option value="all">Tüm Kapsamlar</option>
                            <optgroup label="Fabrikalar" style={{ background: '#1e293b' }}>
                                {factories.map(f => <option key={f.id} value={f.id}>🏭 {f.name}</option>)}
                            </optgroup>
                            <optgroup label="Rampa Grupları" style={{ background: '#1e293b' }}>
                                {doorGroups.map(g => <option key={g.id} value={g.id}>🚪 {g.name}</option>)}
                            </optgroup>
                            <optgroup label="Rampalar" style={{ background: '#1e293b' }}>
                                {docks.map(d => <option key={d.id} value={d.id}>⚓ {d.name}</option>)}
                            </optgroup>
                        </select>
                    </div>
                    {(searchQuery || roleFilter !== 'all' || scopeFilter !== 'all' || transporterFilter !== 'all') && (
                        <Button
                            variant="secondary"
                            style={{ height: '42px', marginBottom: '2px' }}
                            onClick={() => {
                                setSearchQuery('');
                                setRoleFilter('all');
                                setScopeFilter('all');
                                setTransporterFilter('all');
                            }}
                        >
                            ❌ Filtreleri Temizle
                        </Button>
                    )}
                </div>
            </div>

            <div className="card-premium glow-theme" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="table">
                    <thead>
                        <tr>
                            <th>Kullanıcı</th>
                            <th>E-posta</th>
                            <th>Mevcut Rol</th>
                            <th>Kapsam</th>
                            <th>Durum</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <tr key={user.id}>
                                <td>
                                    <div className="flex items-center gap-md">
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: 'var(--primary-gradient)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            color: 'white'
                                        }}>
                                            {user.fullName?.charAt(0) || user.email?.charAt(0)}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{user.fullName}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>ID: {user.id.substring(0, 8)}...</div>
                                        </div>
                                    </div>
                                </td>
                                <td style={{ color: 'var(--text-secondary)' }}>{user.email}</td>
                                <td>
                                    <select
                                        className="select"
                                        style={{
                                            padding: '4px 8px',
                                            fontSize: '12px',
                                            width: 'auto',
                                            background: 'var(--bg-secondary)',
                                            border: '1px solid var(--border-color)'
                                        }}
                                        value={user.role}
                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                        disabled={updatingId === user.id || user.id === userData?.id || !can(PERMISSIONS.USER_MANAGEMENT, PERMISSIONS.EDIT)}
                                    >
                                        {availableRoles.map(role => (
                                            <option key={role} value={role}>{role.replace(/_/g, ' ').toUpperCase()}</option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    {transporterRoles.includes(user.role) ? (
                                        <select
                                            className="select"
                                            style={{
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                width: '100%',
                                                minWidth: '150px',
                                                background: 'var(--bg-secondary)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            }}
                                            value={user.scope?.transporterId || ''}
                                            onChange={(e) => handleInlineTransporterChange(user.id, e.target.value)}
                                            disabled={updatingId === user.id || !can(PERMISSIONS.USER_MANAGEMENT, PERMISSIONS.EDIT)}
                                        >
                                            <option value="">-- Nakliyeci Seçin --</option>
                                            {transporters.map(t => (
                                                <option key={t.id} value={t.id}>{t.companyName}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div
                                            onClick={() => (user.role !== 'coordinator' && user.role !== 'consultant') && openScopeModal(user)}
                                            style={{ cursor: (user.role !== 'coordinator' && user.role !== 'consultant') ? 'pointer' : 'default', minHeight: '32px', display: 'flex', alignItems: 'center' }}
                                            title={(user.role !== 'coordinator' && user.role !== 'consultant') ? "Kapsamı düzenlemek için tıklayın" : ""}
                                        >
                                            {getScopeDisplay(user.scope)}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <span className={`badge ${user.isActive ? 'badge-active' : 'badge-inactive'}`}
                                        style={{
                                            backgroundColor: user.isActive ? 'var(--status-entered)' : 'var(--status-pending)',
                                            color: 'white'
                                        }}>
                                        {user.isActive ? 'Aktif' : 'Pasif'}
                                    </span>
                                </td>
                                <td>
                                    <div className="flex gap-sm">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => openScopeModal(user)}
                                            disabled={updatingId === user.id || user.role === 'coordinator' || user.role === 'consultant' || !can(PERMISSIONS.USER_MANAGEMENT, PERMISSIONS.EDIT)}
                                        >
                                            📍 Kapsam
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleStatusToggle(user.id, user.isActive)}
                                            disabled={updatingId === user.id || user.id === userData?.id || !can(PERMISSIONS.USER_MANAGEMENT, PERMISSIONS.EDIT)}
                                        >
                                            {user.isActive ? 'Durdur' : 'Aktifleştir'}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handlePasswordReset(user.email)}
                                            disabled={updatingId === user.id}
                                            title="Şifre Sıfırlama Maili Gönder"
                                        >
                                            🔑
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Scope Management Modal */}
            <Modal
                isOpen={scopeModal.show}
                onClose={() => setScopeModal({ show: false, user: null })}
                title={`${scopeModal.user?.fullName} - Yetki Kapsamı Atama`}
                footer={
                    <div className="flex justify-end gap-md">
                        <Button variant="secondary" onClick={() => setScopeModal({ show: false, user: null })}>Vazgeç</Button>
                        <Button variant="primary" onClick={handleSaveScope} loading={updatingId === scopeModal.user?.id}>Kaydet</Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-lg">
                    <p className="text-secondary mb-lg" style={{ fontSize: '12px', opacity: 0.8, lineHeight: '1.6' }}>
                        Bu kullanıcının sadece seçilen lokasyonlardaki verileri görmesini ve yönetmesini sağlayabilirsiniz.
                        Hiçbir seçim yapılmazsa, rolüne bağlı olarak tüm verilere erişebilir.
                    </p>

                    {/* Factories */}
                    <div>
                        <h4 className="mb-md flex items-center gap-sm">🏭 Fabrikalar</h4>
                        <MultiSelectCombobox
                            items={factories}
                            selectedItems={selectedScope.factoryIds || []}
                            onChange={(ids) => handleScopeChange('factoryIds', ids)}
                            placeholder="Fabrika ara ve seç..."
                            labelKey="name"
                            valueKey="id"
                        />
                    </div>

                    {/* Door Groups */}
                    <div>
                        <h4 className="mb-md flex items-center gap-sm">🚪 Rampa Grupları</h4>
                        <MultiSelectCombobox
                            items={doorGroups}
                            selectedItems={selectedScope.doorGroupIds || []}
                            onChange={(ids) => handleScopeChange('doorGroupIds', ids)}
                            placeholder="Rampa grubu ara ve seç..."
                            labelKey="name"
                            valueKey="id"
                        />
                    </div>

                    {/* Docks */}
                    <div>
                        <h4 className="mb-md flex items-center gap-sm">⚓ Rampalar</h4>
                        <MultiSelectCombobox
                            items={docks}
                            selectedItems={selectedScope.dockIds || []}
                            onChange={(ids) => handleScopeChange('dockIds', ids)}
                            placeholder="Rampa ara ve seç..."
                            labelKey="name"
                            valueKey="id"
                        />
                    </div>
                </div>
            </Modal>

            {/* Manual Add User Modal */}
            <Modal
                isOpen={addModal}
                closeOnOverlayClick={false}
                onClose={() => setAddModal(false)}
                title="Yeni Kullanıcı Oluştur"
                footer={
                    <div className="flex justify-end gap-md">
                        <Button variant="secondary" onClick={() => setAddModal(false)}>İptal</Button>
                        <Button variant="primary" onClick={handleCreateUser} loading={isProcessing}>Oluştur</Button>
                    </div>
                }
            >
                <form className="flex flex-col gap-lg">
                    <div className="form-group">
                        <label className="form-label">Ad Soyad</label>
                        <input
                            type="text"
                            className="input"
                            placeholder="John Doe"
                            value={newUser.fullName}
                            onChange={e => setNewUser({ ...newUser, fullName: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">E-posta</label>
                        <input
                            type="email"
                            className="input"
                            placeholder="john@example.com"
                            autoComplete="off"
                            name="new_user_email_field_random"
                            value={newUser.email}
                            onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Başlangıç Şifresi</label>
                        <input
                            type="password"
                            className="input"
                            placeholder="******"
                            autoComplete="new-password"
                            name="new_user_password_field_random"
                            value={newUser.password}
                            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Rol</label>
                        <select
                            className="select"
                            value={newUser.role}
                            onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                        >
                            {availableRoles.map(role => (
                                <option key={role} value={role}>{role.replace(/_/g, ' ').toUpperCase()}</option>
                            ))}
                        </select>
                    </div>

                    {/* Transporter Selection for New Transporter User */}
                    {transporterRoles.includes(newUser.role) && (
                        <div className="form-group animated-fade-in">
                            <label className="form-label">Bağlı Nakliyeci <span className="text-danger">*</span></label>
                            <select
                                className="select bg-primary-light"
                                value={newUser.transporterId}
                                onChange={e => setNewUser({ ...newUser, transporterId: e.target.value })}
                                style={{ border: '2px solid var(--primary-color)' }}
                            >
                                <option value="">-- Nakliyeci Seçin --</option>
                                {transporters.map(t => (
                                    <option key={t.id} value={t.id}>{t.companyName}</option>
                                ))}
                            </select>
                            <p className="text-xs text-secondary mt-xs">Kullanıcının sadece kendi firmasının yüklerini görebilmesi için seçiniz.</p>
                        </div>
                    )}
                </form>
            </Modal>

            {/* Excel Upload Confirmation Modal */}
            <Modal
                isOpen={uploadModal.show}
                onClose={() => setUploadModal({ show: false, file: null, parsedUsers: [], progress: 0 })}
                title="Toplu Kullanıcı Aktarımı"
                footer={
                    <div className="flex justify-end gap-md">
                        <Button variant="secondary" onClick={() => setUploadModal({ show: false, file: null, parsedUsers: [], progress: 0 })}>İptal</Button>
                        <Button variant="primary" onClick={handleBulkUpload} loading={isProcessing}>
                            {isProcessing ? `Yükleniyor %${uploadModal.progress}` : `${uploadModal.parsedUsers.length} Kullanıcıyı Aktar`}
                        </Button>
                    </div>
                }
            >
                <div className="text-center p-lg">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                    <h4>{uploadModal.file?.name}</h4>
                    <p className="text-secondary mt-md">
                        Dosyada <strong>{uploadModal.parsedUsers.length}</strong> geçerli kullanıcı bulundu.
                        Onayladığınızda bu kullanıcılar için otomatik hesap oluşturulacaktır.
                    </p>
                    {isProcessing && (
                        <div className="mt-xl w-full bg-white/10 rounded-full h-2 overflow-hidden">
                            <div
                                style={{
                                    width: `${uploadModal.progress}%`,
                                    background: 'var(--primary-gradient)',
                                    height: '100%',
                                    transition: 'width 0.3s ease'
                                }}
                            />
                        </div>
                    )}
                </div>
            </Modal>

            {/* Premium Confirm Dialog */}
            <ConfirmDialog
                {...confirmConfig}
                onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={async () => {
                    if (confirmConfig.action) await confirmConfig.action();
                    setConfirmConfig(prev => ({ ...prev, isOpen: false }));
                }}
            />
        </div>
    );
};

export default UserManagement;
